import { AuthClient, RefreshRequest } from '../web-api-client';
import * as session from './session';

/**
 * Những đường dẫn tự nó là cửa xác thực. Gặp 401 ở đây nghĩa là sai mật khẩu hoặc token làm mới đã
 * hỏng — không phải "access token vừa hết hạn". Thử làm mới ở đây chỉ tạo ra vòng lặp.
 */
const AUTH_PATHS = ['/api/Auth/login', '/api/Auth/register', '/api/Auth/refresh', '/api/Auth/logout'];

/** Client trần, không gắn token và không tự làm mới — dùng riêng cho chính lời gọi làm mới. */
const plainAuthClient = new AuthClient('', {
  fetch: (url: RequestInfo, init?: RequestInit) => window.fetch(url, init)
});

let refreshing: Promise<boolean> | null = null;

function withBearer(init?: RequestInit): RequestInit {
  const active = session.load();
  const headers = new Headers(init?.headers);

  if (active?.accessToken) {
    headers.set('Authorization', `Bearer ${active.accessToken}`);
  }

  return { ...init, headers };
}

async function renewSession(): Promise<boolean> {
  const active = session.load();

  if (!active?.refreshToken) {
    return false;
  }

  try {
    const result = await plainAuthClient.refresh(new RefreshRequest({ refreshToken: active.refreshToken }));
    session.save(session.fromAuthResult(result));
    return true;
  } catch {
    // Token làm mới không còn giá trị: phiên kết thúc. Xóa phiên sẽ báo cho AuthContext, và giao
    // diện tự chuyển về trang đăng nhập.
    session.clear();
    return false;
  }
}

/**
 * Gom nhiều lời gọi làm mới thành một. Một trang thường bắn vài request song song; nếu access token
 * vừa hết hạn thì tất cả cùng nhận 401 cùng lúc. Để chúng cùng làm mới thì token xoay vòng nhiều
 * lần liên tiếp và những lời gọi chậm chân sẽ gửi lên token đã bị thu hồi — máy chủ hiểu đó là dấu
 * hiệu token bị đánh cắp và hủy sạch mọi phiên của tài khoản.
 */
function renewOnce(): Promise<boolean> {
  if (!refreshing) {
    refreshing = renewSession().then(succeeded => {
      refreshing = null;
      return succeeded;
    });
  }

  return refreshing;
}

export const authorizedHttp = {
  async fetch(url: RequestInfo, init?: RequestInit): Promise<Response> {
    const href = typeof url === 'string' ? url : url.url;

    const response = await window.fetch(url, withBearer(init));

    if (response.status !== 401 || AUTH_PATHS.some(path => href.includes(path))) {
      return response;
    }

    if (!(await renewOnce())) {
      return response;
    }

    // Gọi lại đúng một lần với token mới. Body do client sinh ra luôn là chuỗi nên dùng lại được;
    // nếu là stream thì lần gọi thứ hai đã rỗng.
    return window.fetch(url, withBearer(init));
  }
};
